/**
 * Employee benefits enrollment portal.
 *
 * Multi-step flow:
 *   1. Plan comparison — side-by-side card grid grouped by plan_type.
 *   2. Selections — pick plan + tier per type, add dependents inline.
 *   3. Review — totals + binding-elections language.
 *   4. Confirm — sign + submit.
 *
 * The same component renders for new-hire / open / special periods —
 * the backend picks the right one for "me" via
 * /benefits/me/enrollment-context.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/hooks/useToast";
import {
  getMyEnrollmentContext,
  submitMyElections,
  type BenefitPlan,
  type DependentInput,
  type EnrollmentContext,
  type PlanType,
  type Tier,
} from "@/api/benefits";

const TIER_LABELS: Record<Tier, string> = {
  employee_only: "Employee Only",
  employee_spouse: "Employee + Spouse",
  employee_children: "Employee + Children",
  family: "Family",
  waived: "Waived",
};

const PLAN_TYPE_LABELS: Record<string, string> = {
  medical: "Medical",
  dental: "Dental",
  vision: "Vision",
  life: "Life",
  disability_short: "Short-term disability",
  disability_long: "Long-term disability",
  accident: "Accident",
  critical_illness: "Critical illness",
  hsa: "HSA",
  fsa: "FSA",
  retirement_401k: "401(k)",
};

interface ChoiceState {
  plan_id: string | "waive" | null;
  tier: Tier;
  dependents: DependentInput[];
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  });
}

function blankDependent(): DependentInput {
  return { name: "", dob: "", ssn: "", relationship: "spouse" };
}


export function BenefitsEnrollmentPage() {
  const { addToast } = useToast();
  const [ctx, setCtx] = useState<EnrollmentContext | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [choices, setChoices] = useState<Record<string, ChoiceState>>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signature, setSignature] = useState("");

  useEffect(() => {
    let cancelled = false;
    getMyEnrollmentContext()
      .then((c) => !cancelled && setCtx(c))
      .catch(() => {
        if (!cancelled) {
          setCtx({ period: null, plans: [], elections: [], life_events: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Group available plans by plan_type so the comparison + choice
  // surfaces work category-by-category.
  const plansByType = useMemo(() => {
    const out: Record<string, BenefitPlan[]> = {};
    for (const p of ctx?.plans ?? []) {
      (out[p.plan_type] ||= []).push(p);
    }
    return out;
  }, [ctx]);

  // Pre-fill choices from any existing elections so re-opening the
  // page resumes where the user left off.
  useEffect(() => {
    if (!ctx) return;
    const seed: Record<string, ChoiceState> = {};
    for (const t of Object.keys(plansByType)) {
      const existing = ctx.elections.find((el) => {
        const plan = ctx.plans.find((p) => p.id === el.plan_id);
        return plan?.plan_type === t;
      });
      seed[t] = {
        plan_id: existing
          ? existing.plan_id ?? "waive"
          : null,
        tier: existing ? existing.tier : "employee_only",
        dependents: [],
      };
    }
    setChoices(seed);
  }, [ctx, plansByType]);

  if (!ctx) {
    return (
      <Card>
        <div className="py-10 text-center text-text-muted">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      </Card>
    );
  }

  if (!ctx.period) {
    return (
      <motion.div {...pageTransition}>
        <PageHeader title="Benefits enrollment" />
        <Card>
          <div className="py-8 text-center text-sm">
            <CalendarDays className="h-7 w-7 mx-auto mb-2 text-text-muted opacity-60" />
            <p className="text-text-primary font-medium">
              No active enrollment period
            </p>
            <p className="text-text-muted mt-1 mb-5">
              You&rsquo;ll get an email when open enrollment opens or
              you experience a qualifying life event.
            </p>
            <Link
              to="/benefits/life-event"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
            >
              Had a life event? Report it →
            </Link>
          </div>
        </Card>
      </motion.div>
    );
  }

  const period = ctx.period;
  const types = Object.keys(plansByType).sort();

  const totals = types.reduce(
    (acc, t) => {
      const c = choices[t];
      const plan = ctx.plans.find((p) => p.id === c?.plan_id);
      const rate = plan?.tier_rates.find((r) => r.tier === c?.tier);
      if (rate) {
        acc.employee += rate.employee_cost;
        acc.employer += rate.employer_cost;
      }
      return acc;
    },
    { employee: 0, employer: 0 },
  );

  const submit = async () => {
    if (!acknowledged) {
      addToast("error", "You must acknowledge the binding elections language");
      return;
    }
    setSubmitting(true);
    try {
      const payload = types
        .filter((t) => choices[t]?.plan_id != null)
        .map((t) => {
          const c = choices[t]!;
          const planId = c.plan_id === "waive" ? null : c.plan_id;
          return {
            plan_id: planId,
            plan_type: t as PlanType,
            tier: planId ? c.tier : ("waived" as Tier),
            dependents: planId
              ? c.dependents.filter((d) => d.name.trim())
              : undefined,
          };
        });
      await submitMyElections(period.id, payload, signature);
      addToast("success", "Elections submitted");
      const fresh = await getMyEnrollmentContext();
      setCtx(fresh);
      setStep(1);
    } catch {
      addToast("error", "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Benefits enrollment"
        subtitle={`${period.name} — closes ${period.end_date}`}
      />

      {/* Wizard CTA — the recommended path for most employees.
          The existing step-by-step compare/select/review flow below
          stays for power-users and anyone reviewing previously
          submitted elections. */}
      <Link
        to="/benefits/enroll"
        className="block mb-4 rounded-2xl border-2 border-accent/40 bg-gradient-to-br from-accent/5 to-accent/10 p-5 hover:border-accent transition-all group"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="rounded-lg bg-accent text-white p-2.5 shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-text-primary">
                Not sure which plan is right for you?
              </p>
              <p className="text-sm text-text-muted">
                Answer a few quick questions and we&apos;ll recommend
                the best plan based on your usage and budget.
              </p>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-accent shrink-0 group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>

      <Card className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-text-muted">
            Step {step} of 3 ·{" "}
            {step === 1
              ? "Compare plans"
              : step === 2
                ? "Make selections"
                : "Review & confirm"}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            )}
            {step < 3 ? (
              <Button
                size="sm"
                onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
              >
                Next
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button size="sm" onClick={submit} isLoading={submitting}>
                <CheckCircle2 className="h-3.5 w-3.5" />
                Submit elections
              </Button>
            )}
          </div>
        </div>
      </Card>

      {step === 1 && <Step1 plansByType={plansByType} />}

      {step === 2 && (
        <Step2
          plansByType={plansByType}
          choices={choices}
          onChange={setChoices}
        />
      )}

      {step === 3 && (
        <Step3
          ctx={ctx}
          choices={choices}
          totals={totals}
          acknowledged={acknowledged}
          onAcknowledge={setAcknowledged}
          signature={signature}
          onSignature={setSignature}
        />
      )}
    </motion.div>
  );
}


// ── Step 1: comparison ─────────────────────────────────────────────


function Step1({
  plansByType,
}: {
  plansByType: Record<string, BenefitPlan[]>;
}) {
  const types = Object.keys(plansByType).sort();
  return (
    <div className="space-y-4">
      {types.map((t) => (
        <Card key={t}>
          <CardHeader>
            <CardTitle>{PLAN_TYPE_LABELS[t] || t}</CardTitle>
          </CardHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {plansByType[t].map((p) => (
              <div
                key={p.id}
                className="rounded-md border border-border/40 bg-surface/60 p-3"
              >
                <p className="text-sm font-medium text-text-primary">{p.name}</p>
                <p className="text-xs text-text-muted">{p.carrier_name}</p>
                <div className="mt-2 space-y-0.5 text-xs text-text-muted">
                  {p.tier_rates.map((r) => (
                    <div
                      key={r.tier}
                      className="flex justify-between gap-2"
                    >
                      <span>{TIER_LABELS[r.tier]}</span>
                      <span className="text-text-primary tabular-nums">
                        {fmt(r.employee_cost)}/mo
                      </span>
                    </div>
                  ))}
                </div>
                {p.details && Object.keys(p.details).length > 0 && (
                  <div className="mt-3 pt-2 border-t border-border/40 space-y-0.5 text-xs">
                    {Object.entries(p.details).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2">
                        <span className="text-text-muted">
                          {k.replace(/_/g, " ")}
                        </span>
                        <span className="text-text-primary">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {p.document_url && (
                  <a
                    href={p.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-xs text-accent hover:underline"
                  >
                    View plan PDF →
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}


// ── Step 2: selections ─────────────────────────────────────────────


function Step2({
  plansByType,
  choices,
  onChange,
}: {
  plansByType: Record<string, BenefitPlan[]>;
  choices: Record<string, ChoiceState>;
  onChange: (next: Record<string, ChoiceState>) => void;
}) {
  const types = Object.keys(plansByType).sort();

  const update = (t: string, partial: Partial<ChoiceState>) => {
    onChange({
      ...choices,
      [t]: {
        plan_id: choices[t]?.plan_id ?? null,
        tier: choices[t]?.tier ?? "employee_only",
        dependents: choices[t]?.dependents ?? [],
        ...partial,
      },
    });
  };

  const addDep = (t: string) => {
    update(t, {
      dependents: [...(choices[t]?.dependents ?? []), blankDependent()],
    });
  };

  const updateDep = (
    t: string,
    idx: number,
    partial: Partial<DependentInput>,
  ) => {
    const next = (choices[t]?.dependents ?? []).map((d, i) =>
      i === idx ? { ...d, ...partial } : d,
    );
    update(t, { dependents: next });
  };

  const removeDep = (t: string, idx: number) => {
    update(t, {
      dependents: (choices[t]?.dependents ?? []).filter((_, i) => i !== idx),
    });
  };

  return (
    <div className="space-y-4">
      {types.map((t) => {
        const plans = plansByType[t];
        const c = choices[t];
        const showDeps =
          c &&
          c.plan_id !== "waive" &&
          c.plan_id !== null &&
          (c.tier === "employee_spouse"
            || c.tier === "employee_children"
            || c.tier === "family");
        return (
          <Card key={t}>
            <CardHeader>
              <CardTitle>{PLAN_TYPE_LABELS[t] || t}</CardTitle>
            </CardHeader>

            <Select
              label="Plan"
              value={(c?.plan_id ?? "") as string}
              onChange={(e) =>
                update(t, {
                  plan_id: e.target.value as string,
                })
              }
              options={[
                { value: "", label: "— Choose —" },
                ...plans.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.carrier_name})`,
                })),
                { value: "waive", label: "Waive coverage" },
              ]}
            />

            {c?.plan_id && c.plan_id !== "waive" && (
              <Select
                label="Coverage tier"
                value={c.tier}
                onChange={(e) =>
                  update(t, { tier: e.target.value as Tier })
                }
                options={(["employee_only", "employee_spouse", "employee_children", "family"] as Tier[]).map(
                  (tier) => ({
                    value: tier,
                    label: TIER_LABELS[tier],
                  }),
                )}
              />
            )}

            {showDeps && (
              <div className="mt-3 rounded-md border border-border/40 bg-background/40 p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-text-muted">
                    Dependents
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => addDep(t)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </Button>
                </div>
                {(c?.dependents ?? []).length === 0 ? (
                  <p className="text-xs text-text-muted">
                    Add dependents to include on this plan.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {(c?.dependents ?? []).map((d, i) => (
                      <li
                        key={i}
                        className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center"
                      >
                        <Input
                          label="Name"
                          value={d.name}
                          onChange={(e) =>
                            updateDep(t, i, { name: e.target.value })
                          }
                        />
                        <Input
                          label="DOB"
                          type="date"
                          value={d.dob || ""}
                          onChange={(e) =>
                            updateDep(t, i, { dob: e.target.value })
                          }
                        />
                        <Input
                          label="SSN"
                          value={d.ssn || ""}
                          onChange={(e) =>
                            updateDep(t, i, { ssn: e.target.value })
                          }
                          placeholder="XXX-XX-XXXX"
                        />
                        <Select
                          label="Relationship"
                          value={d.relationship || "spouse"}
                          onChange={(e) =>
                            updateDep(t, i, {
                              relationship: e.target
                                .value as DependentInput["relationship"],
                            })
                          }
                          options={[
                            { value: "spouse", label: "Spouse" },
                            { value: "child", label: "Child" },
                            { value: "domestic_partner", label: "Domestic partner" },
                            { value: "other", label: "Other" },
                          ]}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeDep(t, i)}
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-danger" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}


// ── Step 3: review + confirm ───────────────────────────────────────


function Step3({
  ctx,
  choices,
  totals,
  acknowledged,
  onAcknowledge,
  signature,
  onSignature,
}: {
  ctx: EnrollmentContext;
  choices: Record<string, ChoiceState>;
  totals: { employee: number; employer: number };
  acknowledged: boolean;
  onAcknowledge: (v: boolean) => void;
  signature: string;
  onSignature: (v: string) => void;
}) {
  const types = Object.keys(choices).sort();
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-accent" />
            Review your elections
          </span>
        </CardTitle>
      </CardHeader>

      <ul className="divide-y divide-border/40">
        {types.map((t) => {
          const c = choices[t];
          const plan = ctx.plans.find((p) => p.id === c?.plan_id);
          const rate = plan?.tier_rates.find((r) => r.tier === c?.tier);
          return (
            <li key={t} className="py-3">
              <div className="flex justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">
                    {PLAN_TYPE_LABELS[t] || t}
                  </p>
                  <p className="text-xs text-text-muted">
                    {!c?.plan_id
                      ? "No selection"
                      : c.plan_id === "waive"
                        ? "Waived"
                        : `${plan?.name} · ${TIER_LABELS[c.tier]}`}
                  </p>
                  {c?.dependents && c.dependents.length > 0 && (
                    <p className="text-xs text-text-muted mt-0.5">
                      Dependents: {c.dependents.map((d) => d.name).join(", ")}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm tabular-nums text-text-primary">
                    {rate ? fmt(rate.employee_cost) : "—"}
                  </div>
                  <div className="text-xs text-text-muted">/mo</div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 rounded-md border border-accent/30 bg-accent/5 p-3 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-text-muted">Total employee cost</span>
          <span className="font-semibold text-text-primary tabular-nums">
            {fmt(totals.employee)}/mo
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-muted">Employer contribution</span>
          <span className="font-semibold text-text-primary tabular-nums">
            {fmt(totals.employer)}/mo
          </span>
        </div>
        <div className="flex justify-between border-t border-border/40 pt-2">
          <span className="text-text-muted">Annual employee cost</span>
          <span className="font-semibold text-text-primary tabular-nums">
            {fmt(totals.employee * 12)}
          </span>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => onAcknowledge(e.target.checked)}
            className="mt-1 rounded border-border text-accent focus:ring-accent"
          />
          <span className="text-text-primary">
            I understand these elections are binding until the next
            enrollment period unless I experience a qualifying life event.
          </span>
        </label>
        <Input
          label="E-signature (type your full name)"
          value={signature}
          onChange={(e) => onSignature(e.target.value)}
          placeholder="Jane Q. Public"
        />
      </div>
    </Card>
  );
}

export default BenefitsEnrollmentPage;
